import {
  Alert,
  Button,
  Field,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation } from '@tanstack/react-query';
import { type JSX, useState } from 'react';

import { applyCopyWeek, previewCopyWeek, type CopyPlanRow } from '../../../calendar/api';
import { mapMenuCalendarEntryError } from '../../../calendar/calendar-api-errors';

export interface CopyWeekModalPosition {
  id: string;
  name: string;
}

export interface CopyWeekModalProps {
  positions: CopyWeekModalPosition[];
  onClose: () => void;
  onApplied: () => void;
}

/**
 * `menu-calendar/spec.md` → "Copia de planificación entre semanas": two
 * explicit steps, same "preview, then a separate confirming action"
 * wizard shape as `BulkPriceUpdateModal`. The plan is never trusted
 * client-side across the two steps — `handleApply` re-sends the same
 * source range and offset, and `apps/api` recomputes the plan itself
 * (`design.md`: apply "recalcula el plan").
 */
export function CopyWeekModal({ positions, onClose, onApplied }: CopyWeekModalProps): JSX.Element {
  const { toast } = useToast();
  const positionsById: Record<string, CopyWeekModalPosition> = Object.fromEntries(
    positions.map((position) => [position.id, position]),
  );
  const [sourceFrom, setSourceFrom] = useState('');
  const [sourceTo, setSourceTo] = useState('');
  const [targetOffsetDays, setTargetOffsetDays] = useState('7');
  const [overwrite, setOverwrite] = useState(false);
  const [plan, setPlan] = useState<CopyPlanRow[] | null>(null);

  const previewMutation = useMutation({
    mutationFn: () =>
      previewCopyWeek({ sourceFrom, sourceTo, targetOffsetDays: Number(targetOffsetDays) }),
    onSuccess: (result) => setPlan(result),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      applyCopyWeek({
        sourceFrom,
        sourceTo,
        targetOffsetDays: Number(targetOffsetDays),
        overwrite,
      }),
    onSuccess: (result) => {
      toast(
        'success',
        `Copia aplicada: ${result.created} creadas, ${result.replaced} reemplazadas, ${result.skipped} omitidas`,
      );
      onApplied();
      onClose();
    },
  });

  const inputsValid = sourceFrom !== '' && sourceTo !== '' && targetOffsetDays.trim() !== '';

  return (
    <Modal open onClose={onClose} title="Copiar semana" className="max-w-2xl">
      <div className="flex flex-col gap-4">
        {previewMutation.isError ? (
          <Alert tone="danger">{mapMenuCalendarEntryError(previewMutation.error)}</Alert>
        ) : null}
        {applyMutation.isError ? (
          <Alert tone="danger">{mapMenuCalendarEntryError(applyMutation.error)}</Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field
            label="Semana origen (desde)"
            name="sourceFrom"
            type="date"
            value={sourceFrom}
            onChange={(event) => {
              setSourceFrom(event.target.value);
              setPlan(null);
            }}
          />
          <Field
            label="Semana origen (hasta)"
            name="sourceTo"
            type="date"
            value={sourceTo}
            onChange={(event) => {
              setSourceTo(event.target.value);
              setPlan(null);
            }}
          />
          <Field
            label="Desplazamiento en días"
            name="targetOffsetDays"
            type="number"
            value={targetOffsetDays}
            onChange={(event) => {
              setTargetOffsetDays(event.target.value);
              setPlan(null);
            }}
          />
        </div>

        {plan !== null ? (
          <Table>
            <caption className="sr-only">Vista previa de copia de semana</caption>
            <thead>
              <tr>
                <TableHeader>Posición</TableHeader>
                <TableHeader>Fecha origen</TableHeader>
                <TableHeader>Fecha destino</TableHeader>
                <TableHeader>Estado</TableHeader>
              </tr>
            </thead>
            <TableBody>
              {plan.map((row) => (
                <TableRow key={`${row.sourceEntryId}-${row.targetDate}`}>
                  <TableCell>{positionsById[row.positionId]?.name ?? row.positionId}</TableCell>
                  <TableCell>{row.sourceDate}</TableCell>
                  <TableCell>{row.targetDate}</TableCell>
                  <TableCell>
                    <span
                      className={
                        row.status === 'WILL_CREATE'
                          ? 'font-medium text-emerald-700'
                          : row.status === 'POSITION_INACTIVE'
                            ? 'font-medium text-slate-500'
                            : 'font-medium text-amber-700'
                      }
                    >
                      {formatCopyPlanStatus(row.status)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(event) => setOverwrite(event.target.checked)}
            className="size-4 rounded border-slate-300"
          />
          Sobrescribir conflictos
        </label>
        <p className="-mt-2 text-xs text-slate-500">
          Si lo marcás, las entradas que ya existen en las fechas nuevas se reemplazan. Si no, se
          dejan como están.
        </p>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            loading={previewMutation.isPending}
            disabled={!inputsValid}
            onClick={() => previewMutation.mutate()}
          >
            Previsualizar
          </Button>

          {plan !== null ? (
            <Button
              type="button"
              loading={applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
            >
              Confirmar y aplicar
            </Button>
          ) : null}

          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** `menu-calendar/spec.md` → "Vista previa detecta colisión por posición": every plan status gets a plain Spanish label. */
function formatCopyPlanStatus(status: CopyPlanRow['status']): string {
  if (status === 'POSITION_INACTIVE') {
    return 'Posición inactiva, se omite';
  }

  if (status === 'WILL_CREATE') {
    return 'Se crea';
  }

  return 'Ya existe una entrada en esa fecha';
}
